import { useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSocket } from '@/hooks/useSocket'
import { notificationsApi } from '@/api/endpoints/notifications'
import { useNotificationStore } from '@/store/notificationStore'
import { toastService } from '@/hooks/useToast'
import type { Notification } from '@/types'

// ===== Notification list =====

export function useNotifications(params?: {
  page?: number
  size?: number
  unreadOnly?: boolean
  /** Narrow to one workspace; omitted means All Workspaces. Part of `params`,
   *  which is the query key, so each workspace caches separately. */
  workspaceId?: string
}) {
  const queryClient = useQueryClient()
  const { on, off } = useSocket()
  const { addNotification } = useNotificationStore()

  const query = useQuery({
    queryKey: ['notifications', params],
    queryFn: async () => {
      try {
        const res = await notificationsApi.getNotifications(params)
        return res.data
      } catch {
        return {
          data: [] as Notification[],
          total: 0,
          page: 1,
          pageSize: 20,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        }
      }
    },
    staleTime: 30_000,
    // Polling fallback so the notifications list stays fresh even without a socket
    refetchInterval: 45_000,
  })

  // Socket subscription for new notifications
  const handleNewNotification = useCallback(
    (data: unknown) => {
      const notification = data as Notification
      // Add to store
      addNotification({
        id: notification.id,
        title: notification.title,
        message: notification.message,
        type: 'info',
        isRead: false,
        link: notification.link,
        createdAt: notification.createdAt,
        updatedAt: notification.createdAt,
        userId: notification.userId,
      })
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
      // Show toast
      toastService.info(notification.title, 4000)
    },
    [queryClient, addNotification]
  )

  useEffect(() => {
    on('notification:new', handleNewNotification)
    return () => off('notification:new', handleNewNotification)
  }, [on, off, handleNewNotification])

  return {
    notifications: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// ===== Unread count =====

export function useUnreadCount() {
  const queryClient = useQueryClient()
  const { on, off } = useSocket()
  const { unreadCount, setNotifications } = useNotificationStore()

  // The badge has to count the same things the list shows. The list is
  // workspace-filtered server-side; this call was not, so on a workspace holding
  // none of your notifications the bell claimed "1" and the list came back
  // empty — which reads as "notifications are broken" rather than "that one
  // belongs to another workspace".
  //
  // Read straight from the search param rather than through
  // useWorkspaceSelection: the bell renders in the topbar on every page, and
  // waiting on the workspaces query would make the count briefly tenant-wide.
  const [searchParams] = useSearchParams()
  const workspaceId = searchParams.get('workspace') || undefined

  const query = useQuery({
    // workspaceId belongs in the key: without it the first workspace's count is
    // replayed for every other one.
    queryKey: ['notifications', 'unread-count', workspaceId ?? 'all'],
    queryFn: async () => {
      try {
        const res = await notificationsApi.getUnreadCount(workspaceId)
        return res.data.count
      } catch {
        return 0
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    // Polling fallback: if the WebSocket isn't connected (e.g. accessed via LAN IP),
    // the bell still updates within ~45s so new-lead notifications are never missed.
    refetchInterval: 45_000,
    refetchIntervalInBackground: true,
  })

  // Socket subscription to update count in realtime
  const handleNewNotification = useCallback((payload?: any) => {
    queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
    // Backend sends { type: "task_assigned", ... } — refresh tasks immediately
    const type: string = payload?.type || payload?.notificationType || payload?.notification_type || ''
    if (type === 'task_assigned' || type.includes('task')) {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    }
  }, [queryClient])

  const handleNotificationRead = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
  }, [queryClient])

  useEffect(() => {
    on('notification:new', handleNewNotification)
    on('notification:read', handleNotificationRead)
    return () => {
      off('notification:new', handleNewNotification)
      off('notification:read', handleNotificationRead)
    }
  }, [on, off, handleNewNotification, handleNotificationRead])

  return {
    count: query.data ?? unreadCount,
    isLoading: query.isLoading,
  }
}

// ===== Mark as read =====

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  const { markRead } = useNotificationStore()

  const mutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(id),
    onMutate: async (id: string) => {
      // Optimistic update
      markRead(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
    onError: () => {
      // Revert optimistic update by refetching
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  return {
    markRead: mutation.mutateAsync,
    isLoading: mutation.isPending,
  }
}

// ===== Mark all read =====

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  const { markAllRead } = useNotificationStore()

  const mutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: () => {
      // Optimistic update
      markAllRead()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  return {
    markAllRead: mutation.mutateAsync,
    isLoading: mutation.isPending,
  }
}

// ===== Delete notification =====

export function useDeleteNotification() {
  const queryClient = useQueryClient()
  const { removeNotification } = useNotificationStore()

  const mutation = useMutation({
    mutationFn: (id: string) => notificationsApi.deleteNotification(id),
    onMutate: async (id: string) => {
      removeNotification(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  return {
    deleteNotification: mutation.mutateAsync,
    isLoading: mutation.isPending,
  }
}
